import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { AuthToken, Customer } from '@typesApp/Customer'
import { CustomerContext } from '@typesApp/Customer'
import {
  clearLocalCustomerContextState,
  getLocalCustomerContextState,
  isPersistLocalCustomerContext,
  setLocalCustomerContextState,
} from '@utils/localCustomerContext'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { getInsuranceToggleState } from '@utils/insuranceToggleUtil'
import { invokeInsuranceActionListeners } from '@utils/invokeInsuranceListeners'
import { clearLocalAppEventState, getLocalAppEventState, setLocalEventHandled } from '@utils/localAppEventState'
import { useLazyGetGuestActiveDataQuery, useLinkCustomerMutation } from '@redux/queries/guestCustomer'
import { insuranceListenersSelector } from '@redux/slices/insuranceMemberSlice'
import { useLazyGetClaimRecordNoCacheQuery } from '@redux/queries/claimRecords'
import { useCustomer } from './useCustomer'
import { useEnvironment } from './useEnvironment'
import { useRestrictedCheck } from './useRestrictedCheck'

type UseInitTokenActionReturn = {
  beforeInitToken: (customer: Customer, customerContext: CustomerContext) => Promise<void>
  afterInitToken: (customer: Customer, customerContext: CustomerContext, authToken: AuthToken) => Promise<void>
}

export const useInitTokenAction = (): UseInitTokenActionReturn => {
  const { getCustomer } = useCustomer()
  const { anonymousUserId, ecommerceCode, ecommerceTransactionHost } = useEnvironment()
  const [triggerLinkCustomer] = useLinkCustomerMutation()
  const [triggerGetGuestActiveData] = useLazyGetGuestActiveDataQuery()
  const { fetchRestrictedStatus } = useRestrictedCheck()
  const safeInvokeInsuranceListeners = useInvokeInsuranceListenersOnLogin()

  /**
   * Link guest customer to registered customer on login.
   *
   * NOTE: the linkCustomer API is called with the guest customer token
   */
  const linkCustomer = useCallback(
    async (guestCustomerNumber: string, guestCustomerAccessToken: string) => {
      // always fetch latest customer from commerce
      const customer = await getCustomer()
      const {
        id: customerNumber,
        firstName: customerFirstName,
        lastName: customerLastName,
        email: customerEmail,
      } = customer ?? {}

      if (customerNumber) {
        const ecommerce = formatCommerceCode({
          ecommerceCode,
          ecommerceTransactionHost,
        })

        // make sure the claim record still belongs to the guest customer before linking.
        // if the page is reloaded on login, link-customer may have already been called
        // and calling it again will cause an error as the claim record would have been
        // transferred to the registered customer
        const activeData = await triggerGetGuestActiveData({
          guestCustomerNumber,
          guestCustomerAccessToken,
          ecommerce,
        }).unwrap()

        activeData?.id &&
          (await triggerLinkCustomer({
            guestCustomerNumber,
            guestCustomerAccessToken,
            ecommerce,
            customerNumber,
            customerFirstName,
            customerLastName,
            customerEmail,
          }).unwrap())
      }
    },
    [triggerGetGuestActiveData, triggerLinkCustomer, getCustomer, ecommerceCode, ecommerceTransactionHost]
  )

  const linkGuestCustomerOnLogin = useCallback(async () => {
    // need to check from local customer context as commerce may have
    // reloaded the page on customer login
    const localContext = getLocalCustomerContextState()
    const { guestCustomerNumber, accessToken } = localContext ?? {}

    // check if we have a pending login event
    const { type, isHandled } = getLocalAppEventState()
    const isLoginEvent = type === 'onLogin'
    const isLoginEventHandled = isLoginEvent && isHandled

    const shouldLinkCustomer =
      isLoginEvent &&
      !isLoginEventHandled &&
      guestCustomerNumber &&
      accessToken &&
      isCustomerNumberValid(guestCustomerNumber, anonymousUserId)

    // allow error to propagate and cause initToken to fail
    shouldLinkCustomer && (await linkCustomer(guestCustomerNumber, accessToken))

    // mark login event handled only if linkCustomer was successful or not needed
    setLocalEventHandled('onLogin')
  }, [linkCustomer, anonymousUserId])

  /**
   * Note: errors from this call will propagate and cause initToken to fail.
   * If linkCustomer fails, we want the initToken to fail.
   */
  const beforeInitToken = useCallback(
    async (_customer: Customer, _customerContext: CustomerContext) => {
      await linkGuestCustomerOnLogin()
    },
    [linkGuestCustomerOnLogin]
  )

  const handleLoginActions = useCallback(
    async (authToken: AuthToken) => {
      const { type, isHandled } = getLocalAppEventState()
      const isLoginEvent = type === 'onLogin'
      const isLoginEventHandled = isLoginEvent && isHandled

      if (isLoginEvent) {
        await safeInvokeInsuranceListeners(authToken.claimRecordId)
      }

      if (isLoginEventHandled) {
        clearLocalAppEventState()
        clearLocalCustomerContextState()
      }
    },
    [safeInvokeInsuranceListeners]
  )

  /**
   * Auth token has been succesfully fetched - actions within this call should not throw errors
   */
  const afterInitToken = useCallback(
    async (customer: Customer, customerContext: CustomerContext, authToken: AuthToken) => {
      // fetch restricted status should not fail
      fetchRestrictedStatus(customer.id).catch(error =>
        console.warn('directbilling - unable to fetch restricted status', error)
      )

      persistLocalCustomerContext(customerContext, authToken)

      await handleLoginActions(authToken)
    },
    [fetchRestrictedStatus, handleLoginActions]
  )

  return {
    beforeInitToken,
    afterInitToken,
  }
}

/**
 * Persist local state for guest customer so that on next token fetch
 * we have access to the guest customer context in case commerce reloads
 * page on login
 */
const persistLocalCustomerContext = (customerContext: CustomerContext, authToken: AuthToken) => {
  if (isPersistLocalCustomerContext(customerContext, authToken)) {
    setLocalCustomerContextState(authToken)
  }
}

type UseInvokeInsuranceListenersOnLoginReturn = (claimRecordId: string | undefined) => Promise<void>

/**
 * US flow - on login, invoke the apply insurance action if customer has insurance applied
 */
const useInvokeInsuranceListenersOnLogin = (): UseInvokeInsuranceListenersOnLoginReturn => {
  const listenerList = useSelector(insuranceListenersSelector)
  const [fetchClaimRecord] = useLazyGetClaimRecordNoCacheQuery()

  return useCallback(
    async (claimRecordId: string | undefined) => {
      if (!claimRecordId) return

      try {
        const claimRecord = await fetchClaimRecord(claimRecordId).unwrap()
        const { toggleOn } = getInsuranceToggleState({ claimRecord })
        invokeInsuranceActionListeners(listenerList, toggleOn ? 'apply' : 'remove')
      } catch (error) {
        console.warn('Error invoking insurance action listeners', error)
      }
    },
    [fetchClaimRecord, listenerList]
  )
}
